import React, { useEffect, useState } from 'react';
import api from './api';
import { userDisplayName, PRIORITY_BADGE_CLASS, taskCode, formatDate, isOverdue, joursDeRetard } from './helpers';

const COLUMNS = [
  { key: 'Todo', label: 'À faire', dot: 'var(--text-muted)' },
  { key: 'In_Progress', label: 'En cours', dot: 'var(--secondary)' },
  { key: 'In_Review', label: 'Revue', dot: 'var(--accent)' },
  { key: 'Done', label: 'Terminé', dot: 'var(--success)' },
  { key: 'Blocked', label: 'Bloqué', dot: 'var(--danger)' },
];

function Kanban({ tasks, users, onOpenTask, onTaskUpdated }) {
  const [localTasks, setLocalTasks] = useState(tasks);
  const [draggedId, setDraggedId] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    setLocalTasks(tasks);
  }, [tasks]);

  const userName = (id) => userDisplayName(users.find((u) => u.id === id));

  const handleDrop = async (e, columnKey) => {
    e.preventDefault();
    setDragOverColumn(null);
    if (draggedId == null) return;

    const task = localTasks.find((t) => t.id === draggedId);
    if (!task || task.status === columnKey) {
      setDraggedId(null);
      return;
    }

    const previousStatus = task.status;
    setLocalTasks(localTasks.map((t) => (t.id === draggedId ? { ...t, status: columnKey } : t)));
    setDraggedId(null);
    setError('');

    try {
      const res = await api.put(`/tasks/${task.id}`, { status: columnKey });
      if (onTaskUpdated) onTaskUpdated(res.data);
    } catch (err) {
      setLocalTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, status: previousStatus } : t)));
      setError('Impossible de mettre à jour la tâche. Réessayez.');
    }
  };

  return (
    <div className="fade-in">
      {error && <div className="error-message" style={{ marginBottom: 16 }}>{error}</div>}
      <div className="kanban-container">
        {COLUMNS.map((col) => {
          const colTasks = localTasks.filter((t) => t.status === col.key);
          const isOver = dragOverColumn === col.key;

          return (
            <div
              key={col.key}
              className="kanban-column"
              onDragOver={(e) => { e.preventDefault(); setDragOverColumn(col.key); }}
              onDragLeave={() => setDragOverColumn(null)}
              onDrop={(e) => handleDrop(e, col.key)}
              style={isOver ? { borderColor: 'var(--secondary)', background: 'rgba(81,121,151,0.06)' } : undefined}
            >
              <div className="kanban-title">
                <span className="kanban-dot" style={{ background: col.dot }} />
                {col.label} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({colTasks.length})</span>
              </div>

              {colTasks.map((task) => (
                <div
                  key={task.id}
                  className={`kanban-card slide-up ${isOverdue(task) ? 'en-retard' : ''}`}
                  draggable
                  onDragStart={() => setDraggedId(task.id)}
                  onClick={() => onOpenTask(task)}
                >
                  <div className="kanban-card-top">
                    <span className={`badge ${PRIORITY_BADGE_CLASS[task.priority] || 'badge-priority-medium'}`}>
                      {task.priority}
                    </span>
                    <span className="kanban-ref">{taskCode(task.id)}</span>
                  </div>

                  <div className="task-title">{task.name}</div>

                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${task.progress_percent || 0}%` }} />
                  </div>

                  <div className="task-meta-row">
                    <div className="avatar" title={`Responsable : ${userName(task.assigned_to)}`}>
                      {userName(task.assigned_to).substring(0, 2).toUpperCase()}
                    </div>
                    {task.delegated_to && (
                      <div
                        className="avatar avatar-delegue"
                        title={`Déléguée à : ${userName(task.delegated_to)}`}
                      >
                        {userName(task.delegated_to).substring(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div className={`task-meta ${isOverdue(task) ? 'date-retard' : ''}`}
                         title={isOverdue(task) ? `En retard de ${joursDeRetard(task)} jour(s)` : undefined}>
                      📅 {formatDate(task.due_date)}
                    </div>
                  </div>

                  {task.blocking_issue && (
                    <div className="task-meta" style={{ color: 'var(--danger)', marginTop: 8 }}>
                      ⚠️ {task.blocking_issue}
                    </div>
                  )}
                </div>
              ))}

              <button
                className="btn btn-sm btn-block"
                style={{ marginTop: 4, background: 'transparent', borderStyle: 'dashed' }}
                onClick={() => onOpenTask(null, col.key)}
              >
                + Ajouter une tâche
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default Kanban;
